import * as React from "react";
import Title from "src/components/common/Title";
import { Form, Spin, Row, Col } from "antd";
import { FormComponentProps } from "antd/lib/form";
import { PbrSeparator } from "src/components/common/Separator/Separator";
import { ExpenseMaturityDate } from 'src/components/common/ExpenseMaturityDate/ExpenseMaturityDate';
import { PbrInput } from 'src/components/common/PbrInput/PbrInput';
import { PbrInputMoney } from 'src/components/common/PbrInputMoney/PbrInputMoney';
import PbrProcessDocumentUploader, { IFileToUpload } from 'src/components/common/PbrProcessDocumentUploader/PbrProcessDocumentUploader';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import StandardButton from '../StandardButton/StandardButton';
import { useEffect, useState, useContext } from 'react';
import { SelectBank } from '../SelectBank/SelectBank';
import { PbrCard } from '../PbrCard/PbrCard';
import { objectStatePropsUpdate } from 'src/util/objectUtils';
import { ShowNotification } from 'src/components/common/ShowNotification/ShowNotification';
import { ServiceContext } from 'src/services/provider/Service.provider';
import { IExpenseUpdate } from 'src/services/interfaces/Expense/IExpenseUpdate';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IExpenseCreate } from 'src/services/interfaces/Expense/IExpenseCreate';
import { IExpenseDetail } from 'src/services/interfaces/Expense/IExpenseDetail';
import { ExpenseRequestItemEdit } from '../ExpenseRequestItemEdit/ExpenseRequestItemEdit';
import { IExpenseTradeItemCreate } from 'src/services/interfaces/Expense/IExpenseTradeItemCreate';

interface IExpenseCreateOrEditProps extends FormComponentProps, RouteComponentProps<any> {
  title?: string;
  forms: WrappedFormUtils;
  buttonToReturn: JSX.Element;
  onExecuteText: string;
  isCreate: boolean;
  expenseBase?: IExpenseDetail;
  reloadExpense?: (expenseId: number) => void;
}

const ExpenseCreateOrEditForm: React.FC<IExpenseCreateOrEditProps> = ({
  title,
  forms,
  buttonToReturn,
  onExecuteText,
  isCreate,
  expenseBase,
  history,
  reloadExpense
}) => {
  const { expenseRequestService } = useContext(ServiceContext);

  const [hasUploadChange, setHasUploadChange] = useState(false);
  const [expense, setExpense] = useState<IExpenseCreate>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isCreate) {
      fetchEdit();
    }
  }, [expenseBase]);

  const handleExpense = (value: Partial<IExpenseCreate>) => {
    setExpense(objectStatePropsUpdate(value));
  };

  const handleUpload = (fileToUpload: IFileToUpload) => {
    fileToUpload.type = "FiscalDocument";
    handleExpense({ invoiceFileKey: fileToUpload.file, invoiceFileName: fileToUpload.name });
    setHasUploadChange(true);
  }

  function saveOrUpdate() {
    let errorList = null;
    forms.validateFields(async (err, values) => {
      errorList = err;
    });
    const isValid = validateConditions();

    if (isCreate) {
      createExpense(isValid, errorList);
    } else {
      editExpense(isValid, errorList);
    }
  }

  function validateConditions() {
    const itemsIsOk: boolean = !expense.items.some((obj) => obj.listCategories.length === 0);
    let fiscalNoteValuesIsOk: boolean = false;

    if (!itemsIsOk) {
      ShowNotification.Warn("Há itens de trade sem nenhum valor e categoria inserido!", "Insira valor e categoria ao trade, por favor.");
    }
    else {
      fiscalNoteValuesIsOk = validateSumAndTotalValue();

      if (!fiscalNoteValuesIsOk) {
        ShowNotification.Warn("O valor total da NF/ND está incorreto!", "Verifique o valor total da NF/ND  e os valores dos itens da nota.");
      }
    }

    if (!hasUploadChange) {
      ShowNotification.Warn("NF/ND é obrigatório!", "Anexe o NF/ND, por favor.");
    }

    return fiscalNoteValuesIsOk && hasUploadChange && itemsIsOk;
  }

  function validateSumAndTotalValue() {
    const sum = (accumulator, val) => accumulator + val;
    const totalSumValues = expense.items?.length > 0 ? expense.items?.map(o => o.listCategories.map((obj) => obj.value)).flat().reduce(sum) : 0;

    const expenseTotalValue = expense?.totalValue.toFixed(2)
    const expenseItemsSumValue = totalSumValues.toFixed(2);

    return expenseTotalValue === expenseItemsSumValue;
  }

  function fetchEdit() {
    let items: IExpenseTradeItemCreate[] = [];
    const receivedItems: IExpenseTradeItemCreate[] = expenseBase.items.map((obj) => ({
      tradeId: obj.trade.id,
      tradePhase: obj.trade.phase,
      tradeName: obj.trade.tradeOwner.fullName,
      tradeCpf: obj.trade.tradeOwner.cpfCnpj,
      value: obj?.trade?.tradePricing?.pricing?.commission?.officer?.full?.value,
      listCategories: [{ category: obj.category, value: obj.value }]
    }));

    receivedItems.map((obj) => {
      if (items.some((s) => s.tradeId === obj.tradeId)) {
        const notNormalizedItem = items.find((f) => f.tradeId === obj.tradeId);
        const oldList = items.filter((f) => f.tradeId !== obj.tradeId);
        const normalizedItem: IExpenseTradeItemCreate = { ...notNormalizedItem, listCategories: [...notNormalizedItem.listCategories, obj.listCategories[0]] };
        items = [...oldList, normalizedItem];
      } else {
        items.push(obj);
      }
    });

    setExpense(
      {
        bankDataId: expenseBase.bankData.id,
        invoiceFileKey: expenseBase.invoice.fileKey,
        invoiceFileName: expenseBase.invoice.fileName,
        invoiceNumber: expenseBase.invoice.number,
        items,
        maturityDate: expenseBase.invoice.maturityDate,
        totalValue: expenseBase.invoice.totalValue,
      }
    );
  }

  function editExpense(isValid: boolean, errorList: any[]) {
    const request: IExpenseUpdate = { ...expense, expenseId: expenseBase.id };

    if (isValid) {
      if (!errorList) {
        setLoading(true);

        expenseRequestService.putUpdate(request)
          .then((result) => {
            if (result.success) {
              ShowNotification.Success("Solicitação editada.", "Solicitação de pagamento editada com sucesso!")
              reloadExpense(expenseBase.id);
            } else {
              ShowNotification.Error(result?.message ?? "Erro ao salvar nova solicitação", "Erro ao salvar solicitação, tente novamente.");
            }
          })
          .catch((error) => ShowNotification.Error(error?.response?.data?.message ?? "Erro ao salvar nova solicitação", "Erro ao salvar solicitação, tente novamente."))
          .finally(() => setLoading(false));
      }
    }
  }

  function createExpense(isValid: boolean, errorList: any[]) {
    if (isValid) {
      if (!errorList) {
        setLoading(true);

        expenseRequestService.postCreate(expense)
          .then((result) => {
            if (result.success) {
              ShowNotification.Success("Solicitação criada", "Solicitação de pagamento criada com sucesso!")
              history.push("/nf-payment/demmanded", result.data);
            } else {
              ShowNotification.Error(result?.message ?? "Erro ao salvar nova solicitação", "Erro ao salvar solicitação, tente novamente.")
            }
          })
          .catch((error) => ShowNotification.Error(error?.response?.data?.message ?? "Erro ao salvar nova solicitação.", "Erro ao salvar solicitação, tente novamente."))
          .finally(() => setLoading(false));
      }
    }
  }

  return (
    <PbrCard bordered={false}>
      <Spin spinning={loading}>
        <div className="bank-info">
          {title &&
            <>
              <Title size="h4" preset="bolder">
                {title}
              </Title>
              <PbrSeparator />
            </>
          }
          <div className="nf-payment">
            <Form>
              <Row gutter={16}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <ExpenseMaturityDate
                    value={expense?.maturityDate}
                    onChange={(value) => handleExpense({ maturityDate: value })}
                    form={forms}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <PbrInput
                    value={expense?.invoiceNumber}
                    onChange={e => handleExpense({ invoiceNumber: e.target.value })}
                    name="invoiceNumber"
                    title="N° NF/ND"
                    formItemProps={{ label: "N° NF/ND" }}
                    placeholder="Digite o Número da NF/ND"
                    formWrapper={forms}
                    disabled={!isCreate}
                    rules={[{ required: true, message: "Informe o Número da NF/ND" }]}
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <PbrInputMoney
                    name="Valor total"
                    formWrapper={forms}
                    formItemProps={{ label: "Valor total" }}
                    placeholder="Digite o Valor total"
                    value={expense?.totalValue}
                    onChange={e => handleExpense({ totalValue: e })}
                    rules={[{ required: true, message: "Informe o Valor total" }]}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <PbrProcessDocumentUploader
                    isRequired
                    btnText={`Subir NF/ND`}
                    btnOfficer
                    subTitle={"NF/ND"}
                    onChangeBase64={val => handleUpload(val)}
                    style={{ textAlign: "center" }}
                    name={"upload"}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <SelectBank
                    bankDataId={expense?.bankDataId}
                    form={forms}
                    onChange={e => { handleExpense({ bankDataId: Number(e) }) }}
                  />
                </Col>
              </Row>

              <Row gutter={16} type="flex" justify="center">
                <Col xs={24} sm={24} md={24} lg={24}>
                  <ExpenseRequestItemEdit expense={expense} onChange={handleExpense} form={forms} />
                </Col>
              </Row>
            </Form>
          </div>
        </div>
        <PbrSeparator />
        <Row gutter={16} className="actions-btn-row" >
          <Col xs={24} sm={24} md={12} lg={12} style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
            {buttonToReturn}
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
            <StandardButton onClick={() => saveOrUpdate()}>
              {onExecuteText}
            </StandardButton>
          </Col>
        </Row>
      </Spin>
    </PbrCard >
  );
}

export const ExpenseCreateOrEdit = withRouter(Form.create()(ExpenseCreateOrEditForm));



// WEBPACK FOOTER //
// ./src/components/common/ExpenseCreateOrEdit/ExpenseCreateOrEdit.tsx